import { InfoIcon, WarningIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useMemo } from 'react'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useAddProjectMemberships, useGetRepositories } from '../../utils/api-client/api-hooks.ts'
import { getRepositoryProjectMembership, type ProjectResponse } from '../../utils/api-client/api-schemas.ts'
import { joinWithAnd, pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { SearchInput } from '../input/search-input.tsx'
import { ADMIN_REQUIRED_OVERRIDE } from './admin-required-override.ts'
import { toPickableRepository } from './pickable-repositories.ts'
import * as styles from './project-dialog.css.ts'
import { RepositoryPickList } from './repository-pick-list.tsx'
import { SelectionSummary } from './selection-summary.tsx'
import { SELECTION_CAP_REASON, useRepositoryPicker } from './use-repository-picker.ts'

const CANDIDATE_PAGE_SIZE = 1000

export type AddRepositoriesDialogProps = {
  project: Pick<ProjectResponse, 'id' | 'name'>
  onClose: () => unknown
  onAdded: () => unknown
}

/**
 * Adds repositories to a project. Everything not already in this project is on offer — the ungrouped
 * first, since adding one of those is the ordinary case and the rest cost another project a repository.
 */
export const AddRepositoriesDialog: React.FC<AddRepositoriesDialogProps> = ({ project, onClose, onAdded }) => {
  const { addToast } = useToastService()

  const { data: repositories } = useGetRepositories({ pageIndex: 0, pageSize: CANDIDATE_PAGE_SIZE })
  const candidates = useMemo(() => {
    const available = (repositories?._embedded.items ?? []).filter(
      repository => getRepositoryProjectMembership(repository)?.id !== project.id
    )
    // Original order holds within each half, so the list does not reshuffle.
    return [
      ...available.filter(repository => !getRepositoryProjectMembership(repository)),
      ...available.filter(repository => getRepositoryProjectMembership(repository)),
    ].map(toPickableRepository)
  }, [repositories, project.id])
  const fetchedCount = repositories?._embedded.items.length ?? 0
  const unfetched = Math.max(0, (repositories?.total ?? 0) - fetchedCount)
  const picker = useRepositoryPicker({ candidates })

  const moving = useMemo(
    () => candidates.filter(candidate => picker.pickedIds.has(candidate.id) && candidate.currentProjectName),
    [candidates, picker.pickedIds]
  )

  const showError = useShowMutationError({
    prefix: 'Failed to add repositories',
    overrides: ADMIN_REQUIRED_OVERRIDE,
  })
  const assignMutation = useAddProjectMemberships({
    onSuccess: (_data, variables) => {
      addToast({
        message: (
          <>
            {variables.repositoryIds.length} {pluralize(variables.repositoryIds.length, 'repository', 'repositories')}{' '}
            added to &ldquo;{project.name}&rdquo;
          </>
        ),
        variant: 'success',
      })
      onAdded()
    },
    onError: showError,
  })

  const pickedCount = picker.pickedIds.size
  const isPending = assignMutation.isPending

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} onPointerDownOutside={event => event.preventDefault()}>
          <Dialog.Title className={styles.title}>Add repositories to {project.name}</Dialog.Title>
          <Dialog.Description hidden>Pick the repositories to add to this project.</Dialog.Description>

          <SearchInput value={picker.filter} onChange={picker.setFilter} size="small" name="Search repositories" />

          {picker.shownCandidates.length > 0 ? (
            <RepositoryPickList
              label={picker.isFiltering ? 'Matching repositories' : 'Available repositories'}
              candidates={picker.shownCandidates}
              pickedIds={picker.pickedIds}
              onToggle={picker.toggle}
              unpickedDisabledReason={picker.isAtSelectionCap ? SELECTION_CAP_REASON : undefined}
            />
          ) : picker.isFiltering ? (
            <p className={styles.emptyNote}>No repositories match &ldquo;{picker.filter.trim()}&rdquo;.</p>
          ) : (
            <p className={styles.emptyNote}>Every repository is already in this project.</p>
          )}
          {unfetched > 0 && (
            <p className={styles.emptyNote}>
              The organization has {repositories?.total ?? 0} repositories; only the first {fetchedCount} were fetched,
              so {unfetched} cannot be picked here.
            </p>
          )}

          {/* Always on: it describes the dialog, not the selection, so it should not pop in on the first tick. */}
          <div className={styles.infoNote}>
            <InfoIcon size={16} weight="fill" className={styles.infoNoteIcon} aria-hidden />
            <span>Upon adding, repositories will adhere to this project&apos;s feedback.</span>
          </div>

          {moving.length > 0 && (
            <div className={styles.warningNote} role="status">
              <WarningIcon size={16} weight="fill" className={styles.noteIcon} aria-hidden />
              <span>
                {joinWithAnd(moving.map(candidate => candidate.fullName))} {moving.length > 1 ? 'are' : 'is'} already in
                a project. Adding {moving.length > 1 ? 'them' : 'it'} here <strong>moves</strong>{' '}
                {moving.length > 1 ? 'them' : 'it'}.
              </span>
            </div>
          )}

          <div className={styles.bar}>
            <SelectionSummary selectedCount={pickedCount} onClear={picker.clearPicked} />
            <span className={styles.buttons}>
              <GhostButton
                type="secondary"
                className={styles.outlinedGhost}
                onClick={onClose}
                state={isPending ? 'disabled' : 'default'}
              >
                Cancel
              </GhostButton>
              <DefaultButton
                state={isPending ? 'loading' : pickedCount === 0 ? 'disabled' : 'default'}
                onClick={() => assignMutation.mutate({ projectId: project.id, repositoryIds: [...picker.pickedIds] })}
                loadingLabel="Adding repositories"
              >
                Add {pickedCount > 0 ? `${pickedCount} ` : ''}
                {pluralize(pickedCount, 'repository', 'repositories')}
              </DefaultButton>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
